import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus, FaTrash, FaUserCircle } from 'react-icons/fa';
import { getApiEndpoint } from '../../../../config/api';
import { getToken } from '../../../../utils/auth';

function BuildingManagersSettings() {
  const { id: propertyId } = useParams();
  const location = useLocation();
  const propertyIdFromUrl = location.pathname.match(/\/propertydashboard\/(\d+)\/settings/)?.[1]
    || location.pathname.match(/\/properties\/(\d+)\/settings/)?.[1];
  const effectivePropertyId = (propertyId || propertyIdFromUrl) ? parseInt(propertyId || propertyIdFromUrl) : null;

  const [property, setProperty] = useState(null);
  const [buildingManagers, setBuildingManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const buildHeaders = useCallback(() => {
    const t = getToken();
    return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  }, []);

  const fetchProperty = useCallback(async () => {
    if (!effectivePropertyId) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${effectivePropertyId}`), { headers: buildHeaders() });
      if (res.ok) setProperty(await res.json());
    } catch (e) {
      console.error('Failed to fetch property:', e);
    }
  }, [effectivePropertyId, buildHeaders]);

  const fetchBuildingManagers = useCallback(async () => {
    if (!effectivePropertyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${effectivePropertyId}/building-managers`),
        { headers: buildHeaders() }
      );
      if (res.ok) setBuildingManagers(await res.json());
    } catch (e) {
      console.error('Failed to fetch building managers:', e);
    } finally {
      setLoading(false);
    }
  }, [effectivePropertyId, buildHeaders]);

  useEffect(() => { fetchProperty(); }, [fetchProperty]);
  useEffect(() => { fetchBuildingManagers(); }, [fetchBuildingManagers]);

  const handleRemove = async (linkId, name) => {
    if (!window.confirm(`Remove ${name} from this property?`)) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${effectivePropertyId}/building-managers/${linkId}`), {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (res.ok) {
        toast.success('Building manager removed');
        fetchBuildingManagers();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || data.message || 'Failed to remove', { autoClose: 4000 });
      }
    } catch (e) {
      toast.error('Network error', { autoClose: 4000 });
    }
  };

  if (!effectivePropertyId) {
    return (
      <div className="stg-max-wide">
        <p className="stg-page-subtitle">Select a property to manage building managers.</p>
      </div>
    );
  }

  return (
    <div className="stg-max-wide">
      <div className="stg-page-header">
        <h1 className="stg-page-title">Building Managers</h1>
        <p className="stg-page-subtitle">
          Add building managers to this property using their 6-digit CloudSafe ID. Building managers can check in contractors,
          view the job calendar, and manage on-site visitors for this building.
        </p>
      </div>

      <div className="stg-property-info">
        <strong>Property:</strong> {property?.name || 'Loading...'}
      </div>

      <div className="stg-section-header">
        <h2 className="stg-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Assigned Building Managers {buildingManagers.length > 0 && <span className="stg-count">{buildingManagers.length}</span>}
        </h2>
        <button className="stg-btn stg-btn-primary stg-btn-sm" onClick={() => setShowAddModal(true)}>
          <FaPlus /> Add Building Manager
        </button>
      </div>

      {loading ? (
        <div className="stg-loading">Loading...</div>
      ) : buildingManagers.length === 0 ? (
        <div className="stg-empty">
          <FaUserCircle className="stg-empty-icon" />
          <p>No building managers assigned to this property yet.</p>
          <button className="stg-btn stg-btn-outline stg-btn-sm" onClick={() => setShowAddModal(true)}>
            <FaPlus /> Add Building Manager
          </button>
        </div>
      ) : (
        <div className="stg-entity-list">
          {buildingManagers.map(bm => (
            <div className="stg-entity-card" key={bm.link_id}>
              <div className="stg-entity-info">
                <div className="stg-entity-avatar"><FaUserCircle /></div>
                <div>
                  <h3 className="stg-entity-name">{bm.first_name} {bm.last_name}</h3>
                  <p className="stg-entity-detail">{bm.email}</p>
                  {bm.phone_number && <p className="stg-entity-detail">{bm.phone_number}</p>}
                </div>
              </div>
              <button
                className="stg-remove-btn"
                onClick={() => handleRemove(bm.link_id, `${bm.first_name} ${bm.last_name}`)}
                title="Remove"
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddBuildingManagerModal
          propertyId={effectivePropertyId}
          propertyName={property?.name || ''}
          onClose={() => setShowAddModal(false)}
          onAdded={fetchBuildingManagers}
        />
      )}
    </div>
  );
}

function AddBuildingManagerModal({ propertyId, propertyName, onClose, onAdded }) {
  const [buildingManagerId, setBuildingManagerId] = useState('');
  const [step, setStep] = useState('enter');
  const [verified, setVerified] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    const code = buildingManagerId.replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) {
      setError('Building Manager ID must be exactly 6 digits');
      return;
    }
    setError('');
    setVerified(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/building-managers/lookup?building_manager_id=${encodeURIComponent(code)}`),
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (data.found) {
        setVerified(data);
        setStep('verify');
      } else {
        setError(data.message || 'No building manager found with this CloudSafe ID');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAdd = async (e) => {
    e.preventDefault();
    if (!verified) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/building-managers`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ user_id: verified.user_id })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${verified.first_name} ${verified.last_name} added as building manager`);
        onAdded();
        onClose();
      } else {
        setError(data.error || data.message || 'Failed to add building manager');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="stg-modal-overlay" onClick={onClose}>
      <div className="stg-modal" onClick={e => e.stopPropagation()}>
        <div className="stg-modal-header">
          <h3>{step === 'verify' ? 'Confirm building manager' : 'Add Building Manager'}</h3>
          <button type="button" className="stg-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        {step === 'enter' ? (
          <form onSubmit={handleLookup}>
            <div className="stg-modal-body">
              <p className="stg-modal-hint">
                Enter the building manager&apos;s 6-digit CloudSafe ID. They can find this in their account settings.
              </p>
              <div className="stg-form-group">
                <label className="stg-form-label">Property</label>
                <div className="stg-readonly-field">{propertyName}</div>
              </div>
              <div className="stg-form-group">
                <label className="stg-form-label">Building Manager ID (6 digits) <span className="stg-required">*</span></label>
                <input
                  type="text"
                  className="stg-input"
                  placeholder="e.g. 123456"
                  value={buildingManagerId}
                  onChange={e => setBuildingManagerId(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              {error && <p className="stg-error-text">{error}</p>}
            </div>
            <div className="stg-modal-footer">
              <button type="button" className="stg-btn stg-btn-outline stg-btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="stg-btn stg-btn-primary stg-btn-sm" disabled={submitting}>
                {submitting ? 'Looking up\u2026' : 'Find Building Manager'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmAdd}>
            <div className="stg-modal-body">
              <p className="stg-modal-hint">
                Please verify this is the correct building manager before granting access to this property.
              </p>
              <div className="stg-form-group">
                <label className="stg-form-label">Property</label>
                <div className="stg-readonly-field">{propertyName}</div>
              </div>
              <div className="stg-verify-box">
                <p className="stg-verify-label">Name</p>
                <p className="stg-verify-value">{verified?.first_name} {verified?.last_name}</p>
                <p className="stg-verify-label">Email</p>
                <p className="stg-verify-value">{verified?.email}</p>
                <p className="stg-verify-label">CloudSafe ID</p>
                <p className="stg-verify-value stg-verify-code">{verified?.user_code}</p>
              </div>
              {error && <p className="stg-error-text">{error}</p>}
            </div>
            <div className="stg-modal-footer">
              <button type="button" className="stg-btn stg-btn-outline stg-btn-sm" onClick={() => { setStep('enter'); setVerified(null); setError(''); }}>Back</button>
              <button type="submit" className="stg-btn stg-btn-primary stg-btn-sm" disabled={submitting}>
                {submitting ? 'Adding\u2026' : 'Confirm & add'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default BuildingManagersSettings;
